import React from 'react';
import { NavLink } from 'react-router';
import {
  LayoutDashboard,
  Activity,
  Shield,
  Bot,
  Package,
  Monitor,
  Settings,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { isTeamOnlyPath, usePlan } from '@redbax/shared';
import { cn } from '../lib/utils';

const navItems = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/events', label: 'Events', icon: Activity },
  { to: '/policies', label: 'Policies', icon: Shield },
  { to: '/ai-services', label: 'AI Services', icon: Bot },
  { to: '/package-security', label: 'Package Security', icon: Package },
  { to: '/devices', label: 'Devices', icon: Monitor },
  { to: '/settings', label: 'Settings', icon: Settings },
];

export function Sidebar() {
  const [collapsed, setCollapsed] = React.useState(false);
  const { isPersonal } = usePlan();

  // A cut-down nav rather than a full one full of things that refuse (story #134). The free tier keeps
  // the dashboard, events, devices and settings, which is a coherent product: see what was detected, on
  // which machine, under the platform's default policy.
  //
  // The routes themselves stay registered - TeamOnly renders an upgrade panel there - so a bookmark or a
  // shared link still resolves. Hiding the link is presentation; PersonalOrgConfigurationFilter on the
  // server is the actual refusal.
  const items = isPersonal ? navItems.filter((item) => !isTeamOnlyPath(item.to)) : navItems;

  return (
    <aside
      className={cn(
        'flex flex-col border-r border-border bg-surface transition-all duration-200',
        collapsed ? 'w-16' : 'w-56',
      )}
    >
      <nav className="flex-1 space-y-1 p-2 pt-4">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'border-l-2 border-red-accent bg-red-subtle/50 text-text-primary'
                  : 'text-text-secondary hover:bg-surface-raised hover:text-text-primary',
                collapsed && 'justify-center px-2',
              )
            }
          >
            <item.icon className="h-5 w-5 shrink-0" />
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-2">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex w-full items-center justify-center rounded-md p-2 text-text-secondary hover:bg-surface-raised hover:text-text-primary"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>
    </aside>
  );
}
